/**
 * EP-143 T143-014: Gate-chip row for the G0-G8 + P9 gate ladder and the
 * fraud-suspect banner.
 *
 * All rendering is strictly read-only (the operator takes action via
 * VerificationActionModals). Backward compat: when gate_evidence is absent
 * (old reading) nothing renders, no crash, no error.
 *
 * EP-145 A4: the QR-vs-OCR diff block and the disagreement chip used to live
 * here. Both read slip_metadata keys no settlement writer emits, so both
 * rendered null on every payment. The ladder does carry the QR/OCR verdict, but
 * under gate_evidence.g3, which the gate chips already surface.
 */
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui'
import type { GateEvidence, GateEvidenceEntry } from '@/types'

// Ordered display sequence for the gate ladder.
const GATE_KEYS = ['g0', 'g1', 'g2', 'g3', 'g4', 'g5', 'g6', 'g7', 'g8', 'p9'] as const
type GateKey = (typeof GATE_KEYS)[number]

interface GateChipProps {
  gateKey: GateKey
  entry: GateEvidenceEntry
}

function GateChip({ gateKey, entry }: GateChipProps) {
  const { t } = useTranslation('payments')

  const variantMap: Record<GateEvidenceEntry['status'], 'success' | 'error' | 'ghost'> = {
    pass: 'success',
    fail: 'error',
    unavailable: 'ghost',
  }

  const statusLabel: Record<GateEvidenceEntry['status'], string> = {
    pass: t('verification.gate_chip_pass'),
    fail: t('verification.gate_chip_fail'),
    unavailable: t('verification.gate_chip_unavailable'),
  }

  const gateName = t(`verification.gate_names.${gateKey}`)
  const evidenceText = entry.evidence != null ? String(entry.evidence) : undefined
  const title = evidenceText ? `${gateName}: ${statusLabel[entry.status]} — ${evidenceText}` : `${gateName}: ${statusLabel[entry.status]}`

  return (
    <Badge
      variant={variantMap[entry.status]}
      size="sm"
      title={title}
      aria-label={title}
    >
      {gateName}
    </Badge>
  )
}

// ---------------------------------------------------------------------------

interface SlipGateChipsProps {
  gateEvidence: GateEvidence | undefined
}

/**
 * Renders the full G0–G8 + P9 gate chip row.
 * Returns null when gateEvidence is absent (backward compat — old readings).
 */
export function SlipGateChips({ gateEvidence }: SlipGateChipsProps) {
  const { t } = useTranslation('payments')

  if (!gateEvidence || Object.keys(gateEvidence).length === 0) return null

  return (
    <div>
      <dt className="text-sm text-base-content/60 mb-1">
        {t('verification.gate_evidence_section')}
      </dt>
      <dd className="flex flex-wrap gap-1">
        {GATE_KEYS.map((key) => {
          const entry = gateEvidence[key]
          if (!entry) return null
          return <GateChip key={key} gateKey={key} entry={entry} />
        })}
      </dd>
    </div>
  )
}

// ---------------------------------------------------------------------------

interface FraudSuspectBannerProps {
  fraudSuspect: boolean | undefined
}

/**
 * Red alert banner when slip_metadata.fraud_suspect is true.
 * Returns null when the flag is absent or false.
 */
export function FraudSuspectBanner({ fraudSuspect }: FraudSuspectBannerProps) {
  const { t } = useTranslation('payments')

  if (!fraudSuspect) return null

  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-lg border border-error/40 bg-error/10 px-4 py-3 text-sm text-error"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span>{t('verification.fraud_suspect_banner')}</span>
    </div>
  )
}
